import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import hasCustomPaymentCompletePage from 'src/common/components/payment/utils/hasCustomPaymentCompletePage';
import { useRouteProps } from 'src/common/providers/RoutePropsProvider';
import { selectSubscriptionSuccessPartnerConfig } from 'src/common/selectors/config';
import { isLarge, isXLarge } from 'src/common/utils/breakpoints';
import { useBreakpoint } from './useBreakpoint';

export default function useSideBar(isMobile, query, pathname) {
  const { isLanding, hideLeft, hideRight, matchUrl, isDiscord } = useRouteProps();
  const subscriptionSuccessPartnerConfig = useSelector(selectSubscriptionSuccessPartnerConfig);
  const isLargeBreakpoint = useBreakpoint(isLarge);
  // False until the client has measured the viewport, so the right side's ads don't load, and its
  // upsell doesn't report a view, on the server's guess.
  const isRightSideBreakpoint = useBreakpoint(isXLarge, false);
  const isNotLandingOrMobile = !(isLanding || isMobile);
  const isLeftSideDocked = isNotLandingOrMobile && !isDiscord && isLargeBreakpoint;
  // Whether the route has a right side at a wide enough viewport. It renders on the server, with a
  // media query deciding whether it shows and reserving its space, so it paints with the page.
  const hasRightSide = isNotLandingOrMobile && !isDiscord && !hideRight;
  const isRightSideOpen = hasRightSide && isRightSideBreakpoint;
  const [isLeftSideOpen, setIsLeftSideOpen] = useState(isLeftSideDocked);

  const shouldHideLeftSide = hasCustomPaymentCompletePage(
    query,
    pathname,
    subscriptionSuccessPartnerConfig,
  );
  const hideLeftSide = hideLeft || shouldHideLeftSide;

  // Also reset when the viewport crosses the large breakpoint, not only when docking changes. The
  // landing page never docks, so a sidebar opened below the breakpoint would otherwise stay open
  // as a modal after the window widens.
  useEffect(() => {
    setIsLeftSideOpen(hideLeftSide ? false : isLeftSideDocked);
  }, [hideLeftSide, matchUrl, isLeftSideDocked, isLargeBreakpoint]);

  const handleLeftSideToggle = useCallback(() => {
    setIsLeftSideOpen((value) => !value);
  }, []);

  return {
    isLeftSideDocked,
    isLeftSideOpen,
    hasRightSide,
    isRightSideOpen,
    hideLeftSide,
    handleLeftSideToggle,
  };
}
